import { useAuth } from "@/components/Auth";
import { api } from "@/lib/api";
import { ActionIcon, Alert, Button, Group, LoadingOverlay, Modal, Pagination, Paper, Select, Stack, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowsSort, IconKey, IconRefresh, IconSortAscendingLetters, IconSortAscendingNumbers, IconSortDescendingLetters, IconSortDescendingNumbers, IconStarFilled, IconUsersGroup } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import CountySelect from "../CountySelect";

export type UserDashboardResponse = {
    totalUsers: number;
    users: UserDto[];
}

export type UserDto = {
    userId: number;
    userEmail: string;
    role: string;
    firstName: string;
    lastName: string;
    department: string;
    emailNotificationMethods: number;
    smsNotificationMethods: number;
    guardedSubscriptions: number;
    createdAt: string;
    lastLogin: string;
    verified: boolean;
}

const Users = () => {
    const [loading, _] = useState<boolean>(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [sortTerm, setSortTerm] = useState('createdAt desc');
    const [totalUsers, setTotalUsers] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [users, setUsers] = useState<UserDto[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
    const [impersonateModalOpened, impersonateModalHandlers] = useDisclosure(false);
    const [impersonateError, setImpersonateError] = useState<string | null>(null);
    const [resendWelcomeEmailModalOpened, resendWelcomeEmailModalHandlers] = useDisclosure(false);
    const [resendWelcomeEmailError, setResendWelcomeEmailError] = useState<string | null>(null);
    const [promoteUserModalOpened, promoteUserModalHandlers] = useDisclosure(false);
    const [promoteUserError, setPromoteUserError] = useState<string | null>(null);
    const [selectedCounty, setSelectedCounty] = useState<{ name: string; mask: string; format: string; code: string } | null>(null);
    const { impersonate, currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        getUsers();
    }, [])
    useEffect(() => {
        getUsers();
    }, [page, pageSize, sortTerm, searchTerm]);

    const getUsers = async () => {

        try {
            const response: UserDashboardResponse = await api.get(`/admin/users?page=${page}&count=${pageSize}&sortTerm=${sortTerm}&searchTerm=${searchTerm}`);

            setUsers(response.users);
            setTotalUsers(response.totalUsers);
        }
        catch (error) {
            console.error("Error fetching users:", error);
        }
        finally {
        }

    }

    const handleAttemptImpersonateUser = (user: UserDto) => {
        setSelectedUser(user);
        impersonateModalHandlers.open();
    };

    const handleImpersonateUser = async () => {
        if (!selectedUser) return;

        try {
            impersonate(selectedUser.userId);
            impersonateModalHandlers.close();
            navigate(`/dashboard`);
        } catch (error) {
            console.error("Error impersonating user:", error);
            setImpersonateError("Failed to impersonate user. Please try again later.");
        }
    };
    const handleAttemptResendWelcomeEmail = (user: UserDto) => {
        setSelectedUser(user);
        resendWelcomeEmailModalHandlers.open();
    };
    const handleResendWelcomeEmail = async () => {
        if (!selectedUser) return;

        try {
            await api.post(`/admin/resend-welcome`, { userId: selectedUser.userId });
            resendWelcomeEmailModalHandlers.close();
        } catch (error) {
            console.error("Error resending welcome email:", error);
            setResendWelcomeEmailError("Failed to resend welcome email. Please try again later.");
        }
    };
    const handleAttemptPromoteUser = (user: UserDto) => {
        setSelectedUser(user);
        promoteUserModalHandlers.open();
    }
    const handlePromoteUser = async () => {
        setPromoteUserError(null);
        if (!selectedUser || !selectedCounty){
            setPromoteUserError("Please select a county.");
            return;
        }
        if (selectedUser.firstName?.trim() === '' || selectedUser.lastName?.trim() === '' || selectedUser.department?.trim() === '') {
            setPromoteUserError("First Name, Last Name, and Department are required.");
            return;
        }
        try {
            await api.post(`/admin/promote-user`, {
                userId: selectedUser.userId,
                countyCode: selectedCounty.code,
                firstName: selectedUser.firstName,
                lastName: selectedUser.lastName,
                department: selectedUser.department
            });
            promoteUserModalHandlers.close();
            
        } catch (error: any) {
            console.error("Error promoting user:", error);
            setPromoteUserError(`Failed to promote user. ${error.response?.data || error.message}`);
        }
    };

    return (
        <>
            <Paper withBorder p='md' radius={'md'}>
                <Group justify='space-between'>
                    <Title order={4}>Users</Title>
                    <IconUsersGroup size={22} stroke={1.5} />
                </Group>
                <Stack>
                    {loading ? (
                        <LoadingOverlay visible={loading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'xl' }} />
                    ) : (
                        <>
                            {impersonateError && <Alert color='red'>
                                <Text>{impersonateError}</Text>
                            </Alert>}
                            {resendWelcomeEmailError && <Alert color='red'>
                                <Text>{resendWelcomeEmailError}</Text>
                            </Alert>}
                            <Group justify='flex-end' mt='md'>
                                <TextInput
                                    placeholder="Search by email"
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.currentTarget.value)}
                                />
                                <Select
                                    value={pageSize.toString()}
                                    onChange={(value) => setPageSize(Number(value))}
                                    data={[
                                        { value: '10', label: '10' },
                                        { value: '20', label: '20' },
                                        { value: '50', label: '50' },
                                        { value: '100', label: '100' }
                                    ]}
                                />
                                <Pagination
                                    total={Math.ceil(totalUsers / pageSize)}
                                    value={page + 1}
                                    onChange={(value) => {
                                        setPage(value - 1);
                                    }}
                                />
                            </Group>
                            {users.length > 0 && <Table>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                            const newSortTerm = sortTerm.includes('email') ? (sortTerm.includes('desc') ? 'email asc' : 'email desc') : 'email asc';
                                            setSortTerm(newSortTerm);
                                        }}><Text>User Email</Text>{sortTerm.includes('email') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                        <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                            const newSortTerm = sortTerm.includes('createdAt') ? (sortTerm.includes('desc') ? 'createdAt asc' : 'createdAt desc') : 'createdAt asc';
                                            setSortTerm(newSortTerm);
                                        }}><Text>Created At</Text>{sortTerm.includes('createdAt') ? sortTerm.includes('desc') ? <IconSortDescendingNumbers aria-hidden="true" /> : <IconSortAscendingNumbers aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                        <Table.Th><Group><Text>Last Login</Text></Group></Table.Th>
                                        <Table.Th><Group><Text>Verified</Text></Group></Table.Th>
                                        <Table.Th><Group><Text>Guarded Subscriptions</Text></Group></Table.Th>
                                        <Table.Th><Group><Text>Email Notification Methods</Text></Group></Table.Th>
                                        <Table.Th><Group><Text>Actions</Text></Group></Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {users.map((user) => {
                                        if (user?.role?.toLocaleLowerCase() === 'admin' || user?.role?.toLocaleLowerCase() === 'customersupport' && currentUser?.role?.toLocaleLowerCase() !== 'admin') {
                                            return null;
                                        }
                                        return (
                                            <Table.Tr key={user.userId}>
                                                <Table.Td>{user.userEmail}</Table.Td>
                                                <Table.Td>{user.createdAt ? `${dayjs(user.createdAt).format('MM/DD/YYYY')}` : 'N/A'}</Table.Td>
                                                <Table.Td>{user.lastLogin ? `${dayjs(user.lastLogin).format('MM/DD/YYYY')}` : 'N/A'}</Table.Td>
                                                <Table.Td>{user.verified ? 'Yes' : 'No'}</Table.Td>
                                                <Table.Td>{user.guardedSubscriptions}</Table.Td>
                                                <Table.Td>{user.emailNotificationMethods}</Table.Td>
                                                <Table.Td>
                                                    <Group>
                                                        <Tooltip label={`Impersonate ${user.userEmail}`} withArrow>
                                                            <ActionIcon aria-label={`Impersonate ${user.userEmail}`} variant='outline' color='pq-red' onClick={() => handleAttemptImpersonateUser(user)}>
                                                                <IconKey size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                        {currentUser?.role?.toLocaleLowerCase() === 'admin' && <Tooltip label={`Promote ${user.firstName ?? user.userEmail}`} withArrow>
                                                            <ActionIcon aria-label={`Promote ${user.firstName ?? user.userEmail}`} variant='outline' color='pq-red' onClick={() => handleAttemptPromoteUser(user)}>
                                                                <IconStarFilled size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>}
                                                        {!user.verified && <Tooltip label={`Resend Welcome Email to ${user.userEmail}`} withArrow>
                                                            <ActionIcon aria-label={`Resend Welcome Email to ${user.userEmail}`} variant='outline' color='pq-red' onClick={() => handleAttemptResendWelcomeEmail(user)}>
                                                                <IconRefresh size={16} />
                                                            </ActionIcon>
                                                        </Tooltip>}
                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        )
                                    }
                                    )}
                                </Table.Tbody>
                            </Table> }
                            {users.length === 0 && <Alert color="blue" title="No Users Found"></Alert>}
                            <Group justify='flex-end' mt='md'>
                                <Select
                                    value={pageSize.toString()}
                                    onChange={(value) => setPageSize(Number(value))}
                                    data={[
                                        { value: '10', label: '10' },
                                        { value: '20', label: '20' },
                                        { value: '50', label: '50' },
                                        { value: '100', label: '100' }
                                    ]}
                                />
                                <Pagination
                                    total={Math.ceil(totalUsers / pageSize)}
                                    value={page + 1}
                                    onChange={(value) => {
                                        setPage(value - 1);
                                    }}
                                />
                            </Group>
                        </>
                    )}
                </Stack>
            </Paper>
            <Modal opened={impersonateModalOpened} onClose={impersonateModalHandlers.close} title="Impersonate User">
                <Stack>
                    <Text>Are you sure you want to impersonate {selectedUser?.userEmail ?? 'this user'}?</Text>
                    <Group justify='flex-end'>
                        <Button variant="outline" onClick={impersonateModalHandlers.close}>Cancel</Button>
                        <Button onClick={() => handleImpersonateUser()}>Impersonate</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal opened={resendWelcomeEmailModalOpened} onClose={resendWelcomeEmailModalHandlers.close} title="Resend Welcome Email">
                <Stack>
                    <Text>Are you sure you want to resend the welcome email to {selectedUser?.userEmail ?? 'this user'}?</Text>
                    <Group justify='flex-end'>
                        <Button variant="outline" onClick={resendWelcomeEmailModalHandlers.close}>Cancel</Button>
                        <Button onClick={() => handleResendWelcomeEmail()}>Resend</Button>
                    </Group>
                </Stack>
            </Modal>
            <Modal opened={promoteUserModalOpened} onClose={promoteUserModalHandlers.close} title="Promote User">
                <Stack>
                    {promoteUserError && <Alert color='red'>
                        <Text>{promoteUserError}</Text>
                    </Alert>}
                    <Text>Are you sure you want to promote {selectedUser?.userEmail ?? 'this user'}?</Text>
                    <CountySelect label="Select County to Administer" onChange={(county) => {
                        setSelectedCounty(county);
                    }} />
                    <TextInput
                        label="Department"
                        placeholder="Enter department"
                        value={selectedUser?.department || ''}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, department: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="First Name"
                        placeholder="Enter first name"
                        value={selectedUser?.firstName || ''}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, firstName: e.currentTarget.value });
                            }
                        }}
                    />
                    <TextInput
                        label="Last Name"
                        placeholder="Enter last name"
                        value={selectedUser?.lastName || ''}
                        onChange={(e) => {
                            if (selectedUser) {
                                setSelectedUser({ ...selectedUser, lastName: e.currentTarget.value });
                            }
                        }}
                    />
                    <Group justify='flex-end'>
                        <Button variant="outline" onClick={promoteUserModalHandlers.close}>Cancel</Button>
                        <Button onClick={() => handlePromoteUser()}>Promote</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
};

export default Users;